import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Copy, Trash2, Check, ChevronDown } from 'lucide-react';
import { BulkEquipeData } from '@/types/bulk';
import { PictogrammeEquipeSelector } from '@/components/equipe/PictogrammeEquipeSelector';
import { COULEURS_CHANTIER } from '@/constants/couleursChantier';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface BulkEquipeRowProps {
  index: number;
  data: BulkEquipeData;
  onChange: (index: number, data: Partial<BulkEquipeData>) => void;
  onDuplicate: (index: number) => void;
  onDelete: (index: number) => void;
  canDelete: boolean;
  errors: Record<string, string>;
  isFirst?: boolean;
}

export function BulkEquipeRow({
  index,
  data,
  onChange,
  onDuplicate,
  onDelete,
  canDelete,
  errors,
  isFirst,
}: BulkEquipeRowProps) {
  const [colorOpen, setColorOpen] = useState(false);

  const handleColorSelect = (hex: string) => {
    onChange(index, { couleur: hex });
    setColorOpen(false);
  };

  return (
    <div className="grid grid-cols-[30px_1fr_220px_auto_1.2fr_auto] gap-4 items-start p-3 bg-card/50 rounded-lg border border-border/50">
      {/* Numéro de ligne */}
      <span className="text-sm text-muted-foreground font-medium pt-2 text-center">{index + 1}</span>

      {/* Nom équipe */}
      <div>
        <Input
          placeholder="Nom de l'équipe *"
          value={data.nom}
          onChange={(e) => onChange(index, { nom: e.target.value })}
          className={errors.nom ? 'border-red-500' : ''}
        />
        {errors.nom && <span className="text-xs text-red-500">{errors.nom}</span>}
      </div>

      {/* Métier/Pictogramme */}
      <PictogrammeEquipeSelector
        value={data.pictogramme}
        onChange={(pictogramme) => onChange(index, { pictogramme })}
        couleur={data.couleur}
      />

      {/* Couleur - Popover compact */}
      <Popover open={colorOpen} onOpenChange={setColorOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 h-9 px-2 rounded-md border border-border bg-background hover:bg-accent transition-colors"
          >
            <div
              className="w-5 h-5 rounded-full border border-white/20"
              style={{ backgroundColor: data.couleur }}
            />
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="grid grid-cols-5 gap-1.5">
            {COULEURS_CHANTIER.map((couleur) => {
              const isSelected = data.couleur === couleur.hex;
              return (
                <button
                  key={couleur.id}
                  type="button"
                  onClick={() => handleColorSelect(couleur.hex)}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all duration-200 flex items-center justify-center",
                    "hover:scale-110 hover:ring-2 hover:ring-white/50",
                    "focus:outline-none focus:ring-2 focus:ring-white/50",
                    isSelected && "ring-2 ring-white ring-offset-1 ring-offset-background scale-110"
                  )}
                  style={{ backgroundColor: couleur.hex }}
                  title={couleur.label}
                >
                  {isSelected && (
                    <Check className="h-3 w-3 text-white drop-shadow-md" strokeWidth={3} />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Spécialité */}
      <Input
        placeholder="Spécialité (optionnel)"
        value={data.specialite}
        onChange={(e) => onChange(index, { specialite: e.target.value })}
      />

      {/* Actions */}
      <div className="relative flex gap-1">
        {isFirst && (
          <span className="absolute -top-4 left-0 right-0 text-center text-[10px] text-white">Dupliquer</span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onDuplicate(index)}
          title="Dupliquer cette ligne"
        >
          <Copy className="h-4 w-4" />
        </Button>
        {canDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(index)}
            title="Supprimer cette ligne"
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
